/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Box,
    Button,
    Cell,
    Icon,
    NotificationsWrapper,
    NumberField,
    Row,
    Select,
    SelectOption,
    Text,
    TextField,
    useFlags,
} from 'sarsaparilla';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import last from 'lodash/last';
import { gnoment } from 'shared-ui/gnoment';
import { OrderDetailsSection } from 'shared-ui/components/OrderDetailsLayout';
import SelectInput from './inputs/SelectInput';
import TextInput from './inputs/TextInput';
import {
    FacilityDefaults,
    FacilityProps,
    PassInfoProps,
    PassTypeDefaults,
    PassTypeProps,
} from '../props';
import { INTERAGENCY_DISCOUNT_PASSES } from '../constants';
import { passSelectionRouteWithReservation } from '../../dev/routesArray';
import { validToDate } from '../utils/duration_utils';

import StartDateForm from './StartDateForm';
import HolderInfoForm from './HolderInfoForm';
import VehicleInfoForm from './VehicleInfoForm';
import WatercraftInfoForm from './WatercraftInfoForm';
import AttendeeInfoForm from './AttendeeInfoForm';
import OptionSelectForm from './OptionSelectForm';
import { getSeasonDates } from '../utils';
import { ATBNeedToKnow } from './ATBNeedToKnow';

/**
 * NOTE: This component is used by ui-widget. We will need to check ui-widget whenever we do a change here
 */
export default function PassInfoForm({
    disabled,
    facility = FacilityDefaults,
    isInternal,
    isLegacy,
    isPending,
    isScannedPass,
    number = 1,
    onChange = () => {},
    onDiscountBlur = () => {},
    onRemove = () => {},
    passInfo,
    passType = PassTypeDefaults,
    renderModify,
    renderRemove,
    submitAttempted = false,
    reservationId,
    reset,
    isFieldSales,
    dateInfoText,
    renderDateInfoContent,
    isHolderInfoMandatory = true,
    parentSelector,
    isRequired,
    showNonResidentFees = false,
}) {
    const passInfoFormRef = useRef(null);

    const [passInfoState, setPassInfoState] = useState(passInfo);
    const [endDate, setEndDate] = useState(undefined);
    const [startDateIsValid, setStartDateIsValid] = useState(true);
    const [endDateIsValid, setEndDateIsValid] = useState(true);
    const [holderInfoIsValid, setHolderInfoIsValid] = useState(true);
    const [vehicleInfoIsValid, setVehicleInfoIsValid] = useState(true);
    const [watercraftInfoIsValid, setWatercraftInfoIsValid] = useState(true);
    const [optionSelectIsValid, setOptionSelectIsValid] = useState(true);
    const [attendeeInfoIsValid, setAttendeeInfoIsValid] = useState(true);
    const [commercialInfoIsValid, setCommercialInfoIsValid] = useState(true);
    const [interagencyType, setInteragencyType] = useState('');
    const [isAddingDiscount, setIsAddingDiscount] = useState(
        !!get(passInfo, 'features.discount.pass_number', false)
    );

    const formIsValid = () => {
        const features = passType.features;
        let isValid = true;

        if (features['end-date']) isValid = isValid && endDateIsValid;
        if (features['start-date']) isValid = isValid && startDateIsValid;
        if (get(features, ['equipment-info', 'vehicle'])) {
            isValid = isValid && vehicleInfoIsValid;
        }
        if (get(features, ['equipment-info', 'watercraft'])) {
            isValid = isValid && watercraftInfoIsValid;
        }
        if (get(features, ['option-select'])) isValid = isValid && optionSelectIsValid;
        if (features['attendee-info']) isValid = isValid && attendeeInfoIsValid;
        if (features['is-commercial-pass']) {
            isValid = isValid && commercialInfoIsValid;
        }

        return isValid && holderInfoIsValid;
    };

    useEffect(() => {
        if (isInternal && passInfoFormRef.current) {
            passInfoFormRef.current
                .querySelectorAll(
                    'input, textarea, button, select, .rec-select, .rec-dropdown'
                )
                // eslint-disable-next-line no-param-reassign, no-return-assign
                .forEach((el) => (el.tabIndex = -1));
        }
    }, [isInternal]);

    const prevPassInfoRef = useRef(passInfoState);
    const prevValidRef = useRef(formIsValid());
    useEffect(() => {
        const valid = formIsValid();
        const passInfoChanged = !isEqual(passInfoState, prevPassInfoRef.current);
        const validityChanged = valid !== prevValidRef.current;
        if (passInfoChanged || validityChanged) {
            onChange(passType.id, passInfoState, valid);
        }
        prevPassInfoRef.current = passInfoState;
        prevValidRef.current = valid;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        passInfoState,
        passType.id,
        startDateIsValid,
        endDateIsValid,
        holderInfoIsValid,
        vehicleInfoIsValid,
        watercraftInfoIsValid,
        optionSelectIsValid,
        attendeeInfoIsValid,
        commercialInfoIsValid,
    ]);

    useEffect(() => {
        if (reset) {
            setPassInfoState(passInfo);
        }
    }, [reset, passInfo]);

    const onRemoveHandler = () => {
        onRemove(reservationId);
    };

    const handleRemovePass = () => {
        setIsAddingDiscount(false);
        setInteragencyType('');
        setPassInfoState((prev) => ({
            ...prev,
            features: { ...prev.features, discount: { pass_number: '' } },
        }));
    };

    const onModify = () => {
        window.location.assign(
            passSelectionRouteWithReservation(passType.facility_id, reservationId)
        );
    };

    const onStartDateChanged = (startDate, isValid) => {
        setStartDateIsValid(isValid);
        setPassInfoState((prev) => ({
            ...prev,
            start_date: startDate,
            features: { ...prev.features, 'start-date': startDate },
        }));
    };

    const onEndDateChanged = (newEndDate, isValid) => {
        const duration = passInfo?.duration;
        const start_date = passInfo?.start_date;

        if (!start_date || !gnoment(start_date).isValid()) return;

        let endValue = validToDate(start_date, passType);
        const [, seasonEnd] = getSeasonDates(passType?.periods);
        const [year, month, day] = seasonEnd?.split('-') ?? [
            undefined,
            undefined,
            undefined,
        ];
        const adjustedYear = parseInt(year, 10) + 1;

        if (!endValue.isValid()) {
            endValue = gnoment(`${adjustedYear}-${month}-${day}`);
        }

        const endDateValue =
            duration === 'season' || duration === 'seasonal' ? endValue : newEndDate;

        setEndDateIsValid(isValid);
        setEndDate(endDateValue);
        setPassInfoState((prev) => ({
            ...prev,
            endDate: endDateValue,
            features: { ...prev.features, 'end-date': endDateValue },
        }));
    };

    const onDaySegmentsChanged = (daySegmentInfo) => {
        setPassInfoState((prev) => ({
            ...prev,
            features: {
                ...prev.features,
                'day-segment': {
                    ...prev.features['day-segment'],
                    segments: daySegmentInfo,
                },
            },
        }));
    };

    const onVehicleInfoChanged = (vehicleInfo, isValid) => {
        setVehicleInfoIsValid(isValid);
        setPassInfoState((prev) => ({
            ...prev,
            vehicle_info: vehicleInfo,
            features: {
                ...prev.features,
                'equipment-info': {
                    ...prev.features['equipment-info'],
                    vehicle: vehicleInfo,
                },
            },
        }));
    };

    const onWatercraftInfoChanged = (watercraftInfo, isValid) => {
        setWatercraftInfoIsValid(isValid);
        setPassInfoState((prev) => ({
            ...prev,
            watercraft_info: watercraftInfo,
            features: {
                ...prev.features,
                'equipment-info': {
                    ...prev.features['equipment-info'],
                    watercraft: watercraftInfo,
                },
            },
        }));
    };

    const onOptionSelectChange = (selection, isValid) => {
        const { type } = selection;
        setOptionSelectIsValid(isValid);
        setPassInfoState((prev) => {
            const optSelection = get(prev, ['features', 'option-select']) || [];
            return {
                ...prev,
                features: {
                    ...prev.features,
                    'option-select': optSelection
                        .filter((opt) => opt.type !== type)
                        .concat([selection]),
                },
            };
        });
    };

    const onHolderInfoChanged = (holderInfo, isValid) => {
        setHolderInfoIsValid(isValid);
        setPassInfoState((prev) => ({ ...prev, holder_info: holderInfo }));
    };

    const onAttendeeInfoChanged = (attendeeInfo, isValid) => {
        setAttendeeInfoIsValid(isValid);
        const daySegment = passType.features['day-segment']
            ? {
                  'day-segment': {
                      ...passInfoState.features['day-segment'],
                      groups: attendeeInfo,
                  },
              }
            : {};
        setPassInfoState((prev) => ({
            ...prev,
            attendee_info: attendeeInfo,
            features: {
                ...prev.features,
                'attendee-info': attendeeInfo,
                ...daySegment,
            },
        }));
    };

    const onCommercialInfoChanged = (field, value, isValid) => {
        setCommercialInfoIsValid(isValid);
        setPassInfoState((prev) => ({
            ...prev,
            features: { ...prev.features, [field]: value },
        }));
    };

    const onNonResidentInfoChanged = (nonResidentInfo) => {
        setPassInfoState((prev) => ({
            ...prev,
            features: {
                ...prev.features,
                non_resident_fee: {
                    ...prev.features.non_resident_fee,
                    quantity: nonResidentInfo,
                },
            },
        }));
    };

    const handleDiscountChange = (discount) => {
        setPassInfoState((prev) => ({
            ...prev,
            features: { ...prev.features, discount: { pass_number: discount } },
        }));
    };

    const handleHasNonResidentsChange = (e) => {
        const hasNonResidents = e.target.value;
        setPassInfoState((prev) => ({
            ...prev,
            features: {
                ...prev.features,
                non_resident_fee: {
                    ...prev.features.non_resident_fee,
                    quantity: hasNonResidents === 'true' ? 1 : 0,
                    answered: hasNonResidents !== '',
                },
            },
        }));
    };

    const isExistingReservation = !!reservationId && !isPending;

    const lastSegmentDate = last(
        Object.keys(get(passInfoState, ['features', 'day-segment', 'segments'], {}))
    );
    const endDateFromSegments = gnoment(lastSegmentDate).format();

    const holderInfoFilledOut = passInfoState?.holder_info?.first_name;

    const nonResidentFee = passType?.features?.non_resident_fee?.fee;

    // For existing reservations, set the non-resident fee as answered to prevent the dropdown from resetting on edit.
    useEffect(() => {
        if (
            isExistingReservation &&
            showNonResidentFees &&
            nonResidentFee &&
            !passInfoState?.features?.non_resident_fee?.answered
        ) {
            setPassInfoState((prev) => ({
                ...prev,
                features: {
                    ...prev.features,
                    non_resident_fee: {
                        ...prev.features.non_resident_fee,
                        answered: true,
                    },
                },
            }));
        }
    }, [
        isExistingReservation,
        showNonResidentFees,
        nonResidentFee,
        passInfoState?.features?.non_resident_fee?.answered,
    ]);

    const nonResidentQuanity = passInfoState?.features?.non_resident_fee?.quantity ?? 0;
    const nonResidentQuestionAnswered =
        passInfoState?.features?.non_resident_fee?.answered;
    const hasNonResidents = nonResidentQuestionAnswered
        ? nonResidentQuanity === 0
            ? 'false'
            : 'true'
        : '';
    const isCommercialPass = passType?.features?.['is-commercial-pass'];

    return (
        <div
            id={`parkpass-pass-info-form-${number}`}
            className={`parkpass-pass-info-form ${disabled ? 'parkpass-pass-info-form--disabled' : ''}`}
            ref={passInfoFormRef}
        >
            <OrderDetailsSection
                heading={
                    !isInternal && (
                        <span className="parkpass-pass-info-form-heading">
                            <span>{number}.&nbsp;</span>
                            {passType.name}
                            {holderInfoFilledOut && (
                                <span className="parkpass-pass-info-form-heading-passholder">
                                    {`: ${passInfoState?.holder_info.first_name} ${passInfoState?.holder_info.last_name}`}
                                </span>
                            )}
                        </span>
                    )
                }
                onModifyClick={!isInternal && renderModify && onModify}
                onRemoveClick={!isInternal && renderRemove && onRemoveHandler}
                useRemoveConfirmationModal={isExistingReservation}
                parentSelector={parentSelector}
            >
                <div className="parkpass-pass-info-form-form">
                    {isLegacy && (
                        <NotificationsWrapper noticeType="warning" closeButton={false}>
                            <p>
                                This pass was purchased through YourPassNow before Site
                                Pass were offered on {process.env.SITE_NAME}. A refund
                                cannot be issued for this pass as it is a legacy purchase
                                prior to integration with {process.env.SITE_NAME}.
                            </p>
                        </NotificationsWrapper>
                    )}

                    {/* renderAttendeeInfo */}
                    <div>
                        {isInternal && <h3>Pass Holder Details</h3>}
                        <HolderInfoForm
                            number={number}
                            isInternal={isInternal}
                            submitAttempted={submitAttempted}
                            holderInfo={passInfoState?.holder_info}
                            passType={passType}
                            onChange={onHolderInfoChanged}
                            isFieldSales={isFieldSales}
                            isMandatory={isHolderInfoMandatory}
                            showToolTip={
                                showNonResidentFees && passType.duration === 'annual'
                            }
                        />
                        {isInternal && (
                            <div>
                                <h3>Pass Details</h3>
                                <Row>
                                    <Cell width={6}>
                                        <TextInput
                                            id={`facility_name_${number}`}
                                            value={passInfoState?.facility_name}
                                            label="Facility Name"
                                        />
                                    </Cell>
                                    <Cell width={6}>
                                        <TextInput
                                            id={`pass_name_${number}`}
                                            value={passInfoState?.pass_name}
                                            label="Pass Name"
                                        />
                                    </Cell>
                                </Row>
                            </div>
                        )}
                        <StartDateForm
                            isInternal={isInternal}
                            startDateDisabled={false}
                            number={number}
                            isExistingReservation={isExistingReservation}
                            startDate={passInfoState?.features['start-date']}
                            endDate={endDate || endDateFromSegments}
                            segments={get(passInfoState, [
                                'features',
                                'day-segment',
                                'segments',
                            ])}
                            passType={passType}
                            dateInfoText={dateInfoText}
                            renderDateInfoContent={renderDateInfoContent}
                            onChangeStart={onStartDateChanged}
                            onChangeEnd={onEndDateChanged}
                            onChangeDaySegments={onDaySegmentsChanged}
                            onCommercialInfoChanged={onCommercialInfoChanged}
                            commercialInfo={{
                                'duration-of-visit':
                                    passInfoState?.features['duration-of-visit'],
                                'bus-number': passInfoState?.features['bus-number'],
                                'number-of-passengers':
                                    passInfoState?.features['number-of-passengers'],
                            }}
                            facility={facility}
                        />
                    </div>

                    {/* renderVehicleInfo */}
                    {get(passType, ['features', 'equipment-info', 'vehicle']) && (
                        <VehicleInfoForm
                            isScannedPass={isScannedPass}
                            number={number}
                            submitAttempted={submitAttempted}
                            vehicleInfo={
                                passInfoState?.features['equipment-info'].vehicle
                            }
                            passType={passType}
                            onChange={onVehicleInfoChanged}
                        />
                    )}

                    {/* renderWatercraftInfo */}
                    {get(passType, ['features', 'equipment-info', 'watercraft']) && (
                        <WatercraftInfoForm
                            isScannedPass={isScannedPass}
                            number={number}
                            submitAttempted={submitAttempted}
                            watercraftInfo={
                                passInfoState.features['equipment-info'].watercraft
                            }
                            passType={passType}
                            onChange={onWatercraftInfoChanged}
                        />
                    )}

                    {/* renderFeatureAttendeeInfo */}
                    <AttendeeInfoForm
                        number={number}
                        isRequired={isRequired}
                        facilityId={facility?.id}
                        groups={
                            passType?.features['attendee-info'] ||
                            get(passType, ['features', 'day-segment', 'groups'])
                        }
                        attendeeInfo={
                            passInfoState?.features['attendee-info'] ||
                            get(passInfoState, ['features', 'day-segment', 'groups'])
                        }
                        passType={passType}
                        onChange={onAttendeeInfoChanged}
                    />

                    {/* renderOptionSelectInfo */}
                    {get(passType, ['features', 'option-select']) && (
                        <OptionSelectForm
                            options={get(passType, ['features', 'option-select'])}
                            selectedOptions={passInfoState?.features['option-select']}
                            reservationPosition={number}
                            submitAttempted={submitAttempted}
                            onChange={onOptionSelectChange}
                        />
                    )}

                    {/* renderDiscount */}
                    {get(passType, ['features', 'discount']) && !isInternal && (
                        <Row>
                            <Cell width={6}>
                                {isAddingDiscount ? (
                                    <div>
                                        <SelectInput
                                            required
                                            id={`discount_pass_type_${number}`}
                                            label="Pass Type"
                                            value={
                                                interagencyType ||
                                                get(
                                                    passInfoState?.features,
                                                    ['discount', 'pass_type'],
                                                    ''
                                                )
                                            }
                                            options={INTERAGENCY_DISCOUNT_PASSES.map(
                                                (label) => ({
                                                    label,
                                                    value: label,
                                                })
                                            )}
                                            onChange={setInteragencyType}
                                        />
                                    </div>
                                ) : (
                                    <Button
                                        appearance="secondary"
                                        onClick={() => setIsAddingDiscount(true)}
                                        iconBeforeElement={<Icon iconName="cart-add" />}
                                        gaTrackingId="514881135263"
                                    >
                                        Add a Pass Discount
                                    </Button>
                                )}
                            </Cell>
                            {isAddingDiscount && (
                                <Cell width={6}>
                                    <div>
                                        <TextField
                                            isRequired
                                            id={`discount_pass_type_${number}`}
                                            label="Pass ID"
                                            shouldValidateOnUpdate={submitAttempted}
                                            value={get(
                                                passInfoState?.features,
                                                ['discount', 'pass_number'],
                                                ''
                                            )}
                                            onChange={(e) =>
                                                handleDiscountChange(e.target.value)
                                            }
                                            onBlur={onDiscountBlur}
                                        />
                                    </div>
                                </Cell>
                            )}
                        </Row>
                    )}

                    {get(passType, ['features', 'discount']) &&
                        !isInternal &&
                        isAddingDiscount && (
                            <Button
                                appearance="link"
                                iconBeforeElement={<Icon iconName="delete" />}
                                onClick={handleRemovePass}
                            >
                                Remove Pass
                            </Button>
                        )}

                    {/* renderNonResidentInfo */}
                    {showNonResidentFees && nonResidentFee && (
                        <>
                            <Row>
                                <Cell width={12}>
                                    <Select
                                        isRequired
                                        id={`non_resident_selector_${number}`}
                                        label={
                                            isCommercialPass
                                                ? 'Is everyone 16 and older on the tour a U.S. citizen or resident (This does not include driver or tour guide)?'
                                                : 'Are you and everyone you are traveling with a U.S. citizen or resident (only applies to those 16 and over)?'
                                        }
                                        placeholder="Select Option"
                                        value={hasNonResidents}
                                        onChange={handleHasNonResidentsChange}
                                        isInvalid={
                                            submitAttempted &&
                                            !nonResidentQuestionAnswered
                                        }
                                        errorText="Please answer the U.S. citizenship / residency question to proceed to cart"
                                    >
                                        <SelectOption value="true">
                                            No, there are individuals in my party who are
                                            not U.S. citizens or residents.
                                        </SelectOption>
                                        <SelectOption value="false">
                                            Yes, everyone is a U.S. citizen or resident.
                                        </SelectOption>
                                    </Select>
                                </Cell>
                            </Row>
                            {hasNonResidents === 'true' && (
                                <Row className="mt-3">
                                    <Cell width={{ xs: 7, md: 5 }}>
                                        <Text fontWeight="semibold">
                                            {isCommercialPass
                                                ? `How many non-U.S. resident per person $${nonResidentFee} payments need to be added (only applies to those 16 and older)?`
                                                : 'Including yourself, how many non-U.S. residents (16 and over) will be traveling together?'}
                                        </Text>
                                    </Cell>
                                    <Cell width={{ xs: 5, md: 2 }}>
                                        <NumberField
                                            isRequired
                                            id={`non_resident_quantity_${number}`}
                                            units="non residents"
                                            value={nonResidentQuanity}
                                            min={1}
                                            onChange={onNonResidentInfoChanged}
                                        />
                                    </Cell>
                                    <Cell
                                        width={{ xs: 12, md: 5 }}
                                        className="mt-md-0 mt-3"
                                    >
                                        <Box
                                            background="light-yellow"
                                            border="yellow"
                                            padding="sm"
                                        >
                                            <Row>
                                                <Cell width={8}>
                                                    <Text fontWeight="semibold">
                                                        Non-U.S. Resident Fee Subtotal
                                                    </Text>
                                                    <Text size="sm">
                                                        ${nonResidentFee} per non-U.S.
                                                        resident
                                                    </Text>
                                                </Cell>
                                                <Cell width={4}>
                                                    <Text fontWeight="bold" size="xl">
                                                        $
                                                        {nonResidentFee *
                                                            nonResidentQuanity}
                                                    </Text>
                                                </Cell>
                                            </Row>
                                        </Box>
                                    </Cell>
                                </Row>
                            )}
                            <ATBNeedToKnow className="mt-3" />
                            <Alert type="info" className="mt-3">
                                <Text>
                                    NOTE: U.S. citizens or residents 16 or older may be
                                    required to show proof of U.S. citizenship or
                                    residency upon arrival. Example documents include
                                    passport, driver&apos;s license, permanent resident
                                    card.
                                </Text>
                            </Alert>
                        </>
                    )}
                </div>
            </OrderDetailsSection>
        </div>
    );
}

PassInfoForm.propTypes = {
    disabled: PropTypes.bool,
    facility: FacilityProps,
    isInternal: PropTypes.bool,
    isLegacy: PropTypes.bool,
    isPending: PropTypes.bool,
    isScannedPass: PropTypes.bool,
    number: PropTypes.number,
    onChange: PropTypes.func,
    onDiscountBlur: PropTypes.func,
    onRemove: PropTypes.func,
    passInfo: PassInfoProps,
    passType: PassTypeProps,
    renderModify: PropTypes.bool,
    renderRemove: PropTypes.bool,
    submitAttempted: PropTypes.bool,
    reservationId: PropTypes.string,
    reset: PropTypes.bool,
    isFieldSales: PropTypes.bool,
    dateInfoText: PropTypes.string,
    renderDateInfoContent: PropTypes.func,
    isHolderInfoMandatory: PropTypes.bool,
    parentSelector: PropTypes.func,
    isRequired: PropTypes.bool,
    showNonResidentFees: PropTypes.bool,
};

// cspell:ignore parkpass, sitepass
